/**
 * Compute ASTRA cone_vec geometry for the coplanar laminography flow.
 *
 * SOD and ODD are interpreted as axial distances along Z. The laminography
 * tilt creates only an XY orbit shift: for a point at axial distance z from
 * the object plane, the in-plane shift is r = z * tan(tiltX). The detector
 * plane stays flat in the world frame, so U/V pixel axes are constant for
 * all projections.
 *
 * @param {Object} p - Geometry parameters
 * @param {number} p.numOfImgs - Number of projection angles
 * @param {number[]} p.angles - Array of projection angles in degrees
 * @param {number} p.tiltX - Tilt angle around X axis in degrees
 * @param {number} p.sod - Source-to-object distance
 * @param {number} p.sdd - Source-to-detector distance
 * @param {number} p.detPitch - Detector pixel pitch
 * @param {number} [p.offsetU=0] - Detector U offset
 * @param {number} [p.offsetV=0] - Detector V offset
 * @returns {number[][]} Array of rows [Sx Sy Sz Dx Dy Dz Ux Uy Uz Vx Vy Vz]
 */
export function computeCoplanarGeom(p) {
  const deg2rad = (d) => (d * Math.PI) / 180;

  const tiltXRad = deg2rad(p.tiltX);

  const effSod = p.sod;
  const effOdd = p.sdd - p.sod;
  const effPixel = p.detPitch;
  const offsetU = p.offsetU || 0;
  const offsetV = p.offsetV || 0;

  const tanTx = Math.tan(tiltXRad);

  const vecs = [];

  for (let i = 0; i < p.numOfImgs; i++) {
    const phi = -deg2rad(p.angles[i]);
    const cosPhi = Math.cos(phi);
    const sinPhi = Math.sin(phi);

    const row = new Array(12).fill(0);

    // Source center: axial SOD plus tilt-induced XY shift.
    row[0] = -effSod * tanTx * cosPhi;
    row[1] = -effSod * tanTx * sinPhi;
    row[2] = -effSod;

    // Detector center: axial ODD plus matching tilt-induced XY shift.
    row[3] = effOdd * tanTx * cosPhi;
    row[4] = effOdd * tanTx * sinPhi;
    row[5] = effOdd;

    // Flat detector basis in world coordinates.
    row[6] = effPixel;
    row[7] = 0.0;
    row[8] = 0.0;

    row[9] = 0.0;
    row[10] = -effPixel;
    row[11] = 0.0;

    // Detector offsets
    row[3] += offsetU * row[6] + offsetV * row[9];
    row[4] += offsetU * row[7] + offsetV * row[10];
    row[5] += offsetU * row[8] + offsetV * row[11];

    vecs.push(row);
  }

  return vecs;
}

export default computeCoplanarGeom;
